import * as React from "react";
import {
  Typography,
  Box,
  Button,
  Button as MUIButton,
  Grid,
  Skeleton,
  Select,
  MenuItem,
  Collapse,
  IconButton,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { connect } from "react-redux";
import {
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import {
  centered_flex_box,
  main_button,
  MainInput,
} from "../app/components/Styles";
import { Space, Table } from "antd";
import { cancelOrder, exportOrders, getOrders, getOrderStatistics, getSddSettings, sendOrderConfirmation } from "../app/store/actions/dataActions";
import { useEffect } from "react";
import { Close, Search } from "@mui/icons-material";
import moment from "moment";
import { debounce } from "lodash";
import DateRangePicker from "../app/components/DateRangePicker";
import { useTranslation } from 'react-i18next';
import CreateTicketModal from "../app/components/CreateTicketModal";
import { OrderLineItemsExpandedTable } from "../app/components/OrderLineItemsExpandedTable";
import { isEligibleForSendConfirmation } from "../utils/confirmIt";
import { cardShell, StatusPill, formatInt } from "../app/glass/styles";
import { buildOrderHubColumns, mapFulfillmentListRow } from "../app/glass/OrderTableCells";
import SearchFilterBar, { FilterChips, exportIcon, plusIcon } from "../app/glass/SearchFilterBar";
import { patchSearchParams } from "../app/utils/searchParams";
import Hoverable from "../app/glass/Hoverable";

const CANCELLABLE_ORDER_STATUSES = ["New", "Sold Out", "Preparing", "Prepared", "Pending Confirmation", "Unreachable"];

/** Chips that map 1:1 to API `status` query values. */
const STATUS_CHIPS = [
  ['', 'orders.allStatuses'],
  ['New', 'orders.statusNew'],
  ['Preparing', 'orders.statusPreparing'],
  ['Prepared', 'orders.statusPrepared'],
  ['In Transit', 'orders.statusOutForDelivery'],
  ['Delivered', 'orders.delivered'],
  ['Cancelled', 'orders.statusCancelled'],
  ['Pending Confirmation', 'orders.statusPendingConfirmation'],
  ['Unreachable', 'orders.statusUnreachable'],
  ['RTO', 'orders.statusRto'],
  ['Exchanged', 'orders.statusExchanged'],
  ['Sold Out', 'orders.statusSoldOut'],
  ['Invalid', 'orders.statusInvalid'],
];

export const Orders = ({
  getOrders,
  getOrderStatistics,
  getSddSettings,
  user,
  exportOrders,
  sendOrderConfirmation,
  cancelOrder,
  orders,
  orderStatistics,
  isLoading,
  isWaiting,
  token,
  sddOnly = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  /** Keep hub tab: All Orders clears `tab`; SDD keeps `tab=sdd`. */
  const setListParams = React.useCallback((updates) => {
    setSearchParams(patchSearchParams(searchParams, {
      tab: sddOnly ? 'sdd' : undefined,
      ...updates,
    }), { replace: true });
  }, [searchParams, setSearchParams, sddOnly]);
  const sddQuery = sddOnly ? { sameDayDelivery: 'true' } : {};
  const [page, setPage] = React.useState(parseInt(searchParams.get('page')) || 1);

  const sddEnabled = Boolean(user?.sddEnabled);
  const showSddSetup = sddOnly && !sddEnabled;

  // SDD is a different result set — never keep All Orders' page index (empty page + stale total).
  React.useEffect(() => {
    if (!sddOnly) return;
    getSddSettings();
    if (page !== 1) {
      setPage(1);
      setListParams({ page: '1' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sddOnly]);
  const [pageSize, setPageSize] = React.useState(parseInt(searchParams.get('pageSize')) || 10);
  const [startDate, setStartDate] = React.useState(searchParams.get('startDate') ? moment(searchParams.get('startDate')) : null)
  const [endDate, setEndDate] = React.useState(searchParams.get('endDate') ? moment(searchParams.get('endDate')) : null)
  const [search, setSearch] = React.useState(searchParams.get('search') || '');
  const [status, setStatus] = React.useState(searchParams.get('status') || '');
  const [filters, setFilters] = React.useState(searchParams.get('filters') ? JSON.parse(searchParams.get('filters')) : {});
  const [showFilters, setShowFilters] = React.useState(false);
  const [exportModalOpen, setExportModalOpen] = React.useState(false);
  const [exportParams, setExportParams] = React.useState({
    startDate: null,
    endDate: null,
    status: '',
  });
  const [helpTicketOpen, setHelpTicketOpen] = React.useState(false);
  const [helpTicketContext, setHelpTicketContext] = React.useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = React.useState([]);
  const [cancelConfirmOpen, setCancelConfirmOpen] = React.useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();

  const cleanSearchParams = (params) => {
    const cleaned = {};
    Object.keys(params).forEach((key) => {
      const value = params[key];
      if (value !== undefined && value !== null && value !== '' && value !== 'undefined') {
        cleaned[key] = value;
      }
    });
    return cleaned;
  };

  const getListRefreshParams = React.useCallback(() => ({
    startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
    endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
    page,
    limit: pageSize,
    ...(status && { status }),
    ...(search && { search }),
    ...sddQuery,
  }), [startDate, endDate, page, pageSize, status, search, sddOnly]);

  const handleSendConfirmation = (orderId) => {
    sendOrderConfirmation({ orderId }, getListRefreshParams());
  };

  const handleBulkCancel = () => {
    const ids = selectedRowKeys.slice();
    if (!ids.length) return;
    cancelOrder({ orderIds: ids }, null, getListRefreshParams()).then((ok) => {
      if (ok) {
        setSelectedRowKeys([]);
        setCancelConfirmOpen(false);
      }
    });
  };

  const renderSendConfirmationButton = (record) => {
    if (!isEligibleForSendConfirmation({
      status: record.orderStatus,
      confirmedAt: record.confirmedAt,
      financialStatus: record.financialStatus,
    }, user)) {
      return null;
    }

    return (
      <Button
        size="small"
        sx={{ ...main_button, textTransform: "none", py: 0.5, px: 1, fontSize: "0.75rem", width: isMobile ? '100%' : 'auto' }}
        onClick={() => handleSendConfirmation(record._id)}
        disabled={isWaiting}
      >
        {t('orders.sendConfirmation')}
      </Button>
    );
  };

  // Add handlers for filter changes
  const handleSearchChange = (value) => {
    setPage(1);
    setSearch(value);
    setListParams({
      page: '1',
      pageSize: pageSize.toString(),
      startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
      endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
      search: value,
      status,
      filters: JSON.stringify(filters)
    });
  };

  const handleStatusChange = (value) => {
    setPage(1);
    setStatus(value);
    setListParams({
      page: '1',
      pageSize: pageSize.toString(),
      startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
      endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
      search,
      status: value,
      filters: JSON.stringify(filters)
    });
  };

  const handleDateRangeChange = ({ startDate: newStartDate, endDate: newEndDate }) => {
    setPage(1);
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    setListParams({
      page: '1',
      pageSize: pageSize.toString(),
      startDate: newStartDate ? newStartDate.format('YYYY-MM-DD') : null,
      endDate: newEndDate ? newEndDate.format('YYYY-MM-DD') : null,
      search,
      status,
      filters: JSON.stringify(filters)
    });
  };

  // Restore state when returning from order view
  useEffect(() => {
    if (location.state?.from === '/orders' && location.state?.state) {
      const { page, pageSize, startDate, endDate, search, status, filters } = location.state.state;
      setPage(page);
      setPageSize(pageSize);
      setStartDate(startDate ? moment(startDate) : null);
      setEndDate(endDate ? moment(endDate) : null);
      setSearch(search || '');
      setStatus(status || '');
      setFilters(JSON.parse(filters));
      setListParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        startDate,
        endDate,
        search,
        status,
        filters
      });
    }
  }, [location.state]);

  // Add new useEffect for date validation
  useEffect(() => {
    if (showSddSetup) return;
    if ((startDate && !endDate) || (!startDate && endDate)) {
      return; // Don't proceed if only one date is set
    }
    const params = {
      startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
      endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
      page,
      limit: pageSize,
      ...(status && { status }),
      ...(search && { search }),
      ...sddQuery,
    };
    console.log(params);
    getOrders(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, startDate, endDate, status, search, sddOnly, showSddSetup])

  useEffect(() => {
    if (showSddSetup) return;
    if ((startDate && !endDate) || (!startDate && endDate)) {
      return; // Don't proceed if only one date is set
    }
    const params = {
      startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
      endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
      ...sddQuery,
    };
    console.log(params);
    getOrderStatistics(params);
  }, [startDate, endDate, sddOnly, showSddSetup])

  React.useEffect(() => {
    setSelectedRowKeys([]);
  }, [page, pageSize, status, search, startDate, endDate]);

  const handleFilter = () => {
    setPage(1);
    setListParams({
      page: '1',
      pageSize: pageSize.toString(),
      startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
      endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
      search,
      status,
      filters: JSON.stringify(filters)
    });
  }

  const handleTableChange = (pagination, filters, sorter) => {
    setPage(pagination.current);
    setPageSize(pagination.pageSize);
    setFilters(filters);
    setListParams({
      page: pagination.current.toString(),
      pageSize: pagination.pageSize.toString(),
      startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
      endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
      search,
      status,
      filters: JSON.stringify(filters)
    });
  }

  const debouncedSearch = React.useCallback(
    debounce((searchTerm) => {
      if (searchTerm.length >= 3 || searchTerm.length === 0) {
        setPage(1);
        setListParams({
          page: '1',
          pageSize: pageSize.toString(),
          startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
          endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
          search: searchTerm,
          status,
          filters: JSON.stringify(filters)
        });
      }
    }, 500),
    [pageSize, startDate, endDate, status, filters]
  );

  const handleSearch = () => {
    setPage(1);
    const params = {
      startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
      endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
      page: 1,
      limit: pageSize,
      ...(search && { search }),
      ...(status && { status }),
      ...sddQuery,
    };
    getOrders(params);
    setListParams({
      page: '1',
      pageSize: pageSize.toString(),
      startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
      endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
      ...(search && { search }),
      ...(status && { status }),
      filters: JSON.stringify(filters)
    });
  };

  const clearAllFilters = () => {
    setSearch('');
    setStatus('');
    setStartDate(null);
    setEndDate(null);
    setFilters({});
    setPage(1);
    setListParams({
      page: '1',
      pageSize: pageSize.toString()
    });
  };

  const getCancelledByLabel = (cancelledByValue) => {
    const normalizedValue = String(cancelledByValue || "").trim().toLowerCase();

    if (normalizedValue === "customer") return "Customer";
    if (normalizedValue === "partner") return "Partner";
    if (normalizedValue === "confirmit") return "ConfirmIt";

    return cancelledByValue || "Partner";
  };

  const renderStatusCell = (_, { status, confirmedAt, cancelledAt, cancelledBy }) => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignItems: "flex-start" }}>
      <StatusPill status={status}>{status || "-"}</StatusPill>
      {(confirmedAt || cancelledAt) && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {confirmedAt && status !== "Cancelled" && !cancelledAt && (
            <StatusPill status="Confirmed">CONFIRMED</StatusPill>
          )}
          {cancelledAt && (
            <StatusPill status="Cancelled">
              {`CANCELLED BY: ${getCancelledByLabel(cancelledBy).toUpperCase()}`}
            </StatusPill>
          )}
        </Box>
      )}
    </Box>
  );

  const renderActions = (_, record) => (
    <Space wrap>
      <Button
        size="small"
        sx={{ ...main_button, textTransform: "none", py: 0.5, px: 1, fontSize: "0.75rem", width: isMobile ? '100%' : 'auto' }}
        onClick={() => {
          const currentState = {
            page,
            pageSize,
            startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
            endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
            search,
            status,
            filters: JSON.stringify(filters)
          };
          navigate(`/orders/${record._id}`, { state: { from: '/orders', state: currentState } });
        }}
      >
        {t('orders.viewOrder')}
      </Button>
      {renderSendConfirmationButton(record)}
      <Button
        size="small"
        sx={{ ...main_button, textTransform: "none", py: 0.5, px: 1, fontSize: "0.75rem", width: isMobile ? '100%' : 'auto' }}
        onClick={() => { setHelpTicketContext({ ticketType: 'Order', relevantOrder: record._id }); setHelpTicketOpen(true); }}
      >
        {t('helpDesk.getHelp')}
      </Button>
    </Space>
  );

  const columns = buildOrderHubColumns({
    t,
    isMobile,
    renderStatus: renderStatusCell,
    renderActions,
  });

  const expandedRowRender = (record) => (
    <OrderLineItemsExpandedTable items={record.items} showHeader={!isMobile} />
  );

  // If URL/page is past the last page (e.g. switched to SDD with ?page=2), clamp so the table is not empty with a non-zero total.
  React.useEffect(() => {
    const totalDocs = orders?.totalDocs;
    const limit = Number(orders?.limit) || pageSize;
    if (totalDocs == null || page <= 1) return;
    const maxPage = Math.max(1, Math.ceil(Number(totalDocs) / limit) || 1);
    if (page > maxPage) {
      setPage(1);
      setListParams({ page: '1' });
    }
  }, [orders?.totalDocs, orders?.limit, page, pageSize, setListParams]);

  const data = (orders?.docs || []).map((row) => mapFulfillmentListRow(row, {
    t,
    vendor: user?.vendor,
    typeLabel: t('orders.typeDeliver'),
  }));

  const handleExportToExcel = () => {
    setExportParams({
      startDate: startDate && startDate.isValid() ? startDate : moment().startOf('month'),
      endDate: endDate && endDate.isValid() ? endDate : moment().endOf('month'),
      status: status || '',
    });
    setExportModalOpen(true);
  };

  const handleExportConfirm = () => {
    const params = cleanSearchParams({
      startDate: exportParams.startDate?.isValid() ? exportParams.startDate.format('YYYY-MM-DD') : undefined,
      endDate: exportParams.endDate?.isValid() ? exportParams.endDate.format('YYYY-MM-DD') : undefined,
      status: exportParams.status || undefined,
      search: search || undefined,
      ...(sddOnly ? { sameDayDelivery: true } : {}),
    });
    exportOrders(params);
    setExportModalOpen(false);
  };

  const handleExportDateRangeChange = ({ startDate: newStartDate, endDate: newEndDate }) => {
    setExportParams({
      ...exportParams,
      startDate: newStartDate || null,
      endDate: newEndDate || null,
    });
  };

  const handleExportStatusChange = (value) => {
    setExportParams({ ...exportParams, status: value });
  };

  const pct = (num, den) =>
    num != null && den ? (((Number(num) / Number(den)) * 100) || 0).toFixed(1) : '0.0';

  const kpiItems = [
    {
      label: t('orders.totalOrders'),
      value: formatInt(orderStatistics?.totalOrders),
      sub: `${orderStatistics?.growthPercentage > 0 ? `+${orderStatistics?.growthPercentage}` : orderStatistics?.growthPercentage ?? 0}% ${t('orders.sinceLastMonth')}`,
    },
    {
      label: t('orders.shippedOrders'),
      value: formatInt(orderStatistics?.totalShipped),
      sub: `${pct(orderStatistics?.totalShipped, orderStatistics?.totalOrders)}% ${t('orders.ofTotalOrdersInTransit')}`,
    },
    {
      label: t('orders.deliveredOrders'),
      value: formatInt(orderStatistics?.totalDelivered),
      sub: `${pct(orderStatistics?.totalDelivered, (orderStatistics?.totalDelivered || 0) + (orderStatistics?.totalReturned || 0))}% ${t('orders.ofShippedOrdersDelivered')}`,
    },
    {
      label: t('orders.returnedOrders'),
      value: formatInt(orderStatistics?.totalReturned),
      sub: `${pct(orderStatistics?.totalReturned, (orderStatistics?.totalDelivered || 0) + (orderStatistics?.totalReturned || 0))}% ${t('orders.ofShippedOrdersReturned')}`,
    },
    {
      label: t('orders.cancelledOrders'),
      value: formatInt(orderStatistics?.totalCancelled),
      sub: `${pct(orderStatistics?.totalCancelled, orderStatistics?.totalOrders)}% ${t('orders.ofTotalOrdersCancelled')}`,
    },
  ];

  const sddSetupSteps = [
    { title: t('orders.sddSetupStep1Title'), body: t('orders.sddSetupStep1Body') },
    { title: t('orders.sddSetupStep2Title'), body: t('orders.sddSetupStep2Body') },
    { title: t('orders.sddSetupStep3Title'), body: t('orders.sddSetupStep3Body') },
    { title: t('orders.sddSetupStep4Title'), body: t('orders.sddSetupStep4Body') },
  ];

  if (showSddSetup) {
    return (
      <div>
        <div style={{ ...cardShell, maxWidth: 720 }}>
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>{t('orders.sddSetupTitle')}</div>
          <p style={{ margin: '0 0 20px', fontSize: 13.5, lineHeight: 1.65, color: 'var(--tx2)' }}>
            {t('orders.sddSetupIntro')}
          </p>
          <ol
            style={{
              margin: 0,
              padding: 0,
              listStyle: 'none',
              display: 'flex',
              flexDirection: 'column',
              gap: 14,
              color: 'var(--tx2)',
              fontSize: 13.5,
              lineHeight: 1.65,
            }}
          >
            {sddSetupSteps.map((step, index) => (
              <li key={step.title} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <span
                  style={{
                    flexShrink: 0,
                    width: 26,
                    height: 26,
                    borderRadius: 9,
                    display: 'grid',
                    placeItems: 'center',
                    fontSize: 12,
                    fontWeight: 700,
                    color: 'var(--red-tx)',
                    background: 'rgba(125,1,12,0.28)',
                    border: '1px solid rgba(190,50,62,0.40)',
                  }}
                >
                  {index + 1}
                </span>
                <span style={{ paddingTop: 2 }}>
                  <strong style={{ color: 'var(--tx)' }}>{step.title}</strong>
                  <div style={{ marginTop: 4 }}>{step.body}</div>
                </span>
              </li>
            ))}
          </ol>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 22 }}>
            <a
              className="cz-chip red"
              href="https://apps.shopify.com/chainz-warehousing"
              target="_blank"
              rel="noreferrer"
              style={{ textDecoration: 'none' }}
            >
              {t('orders.sddSetupOpenAppStore')}
            </a>
            <Hoverable
              as="button"
              type="button"
              className="cz-chip"
              hover={{}}
              onClick={() => navigate('/settings?tab=shopify')}
            >
              {t('orders.sddSetupOpenShopifySettings')}
            </Hoverable>
            <Hoverable
              as="button"
              type="button"
              className="cz-chip"
              hover={{}}
              onClick={() => getSddSettings()}
            >
              {t('orders.sddSetupRefresh')}
            </Hoverable>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Box sx={{ width: "100%", display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div
          className="cz-g2"
          style={{
            background: 'var(--card-solid)',
            borderRadius: 26,
            padding: isMobile ? '18px 20px' : '24px 34px',
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(5, 1fr)',
            gap: 24,
            alignItems: 'center',
            border: '1px solid var(--s12)',
            boxShadow: 'inset 0 1px 0 var(--s14), 0 22px 50px var(--sh45)',
          }}
        >
          {kpiItems.map((st) => (
            <div key={st.label}>
              <div style={{ fontSize: 12, color: 'var(--mut2)', marginBottom: 10 }}>{st.label}</div>
              <div style={{ fontSize: isMobile ? 22 : 28, fontWeight: 500 }}>
                {isLoading ? <Skeleton width={48} /> : st.value}
              </div>
              <div style={{ fontSize: 11, color: 'var(--mut2)', marginTop: 6 }}>{st.sub}</div>
            </div>
          ))}
        </div>

        <div style={{ fontSize: 12, color: 'var(--mut)' }}>
          {t('orders.statisticsDateRangeNote', {
            from: startDate ? startDate.format("YYYY-MM-DD") : moment().startOf("month").format("YYYY-MM-DD"),
            to: endDate ? endDate.format("YYYY-MM-DD") : moment().endOf("month").format("YYYY-MM-DD")
          })}
        </div>

        <FilterChips
          value={status}
          onChange={handleStatusChange}
          options={STATUS_CHIPS.map(([value, labelKey]) => [value, t(labelKey, { defaultValue: value || 'All' })])}
        />

        <SearchFilterBar
          placeholder={t('orders.searchPlaceholder') || 'Search by order number or customer information'}
          value={search}
          onChange={handleSearchChange}
          onSearch={handleSearch}
          isMobile={isMobile}
          filterOpen={showFilters}
          onToggleFilter={() => setShowFilters(!showFilters)}
          filterLabel={t('common.filter')}
          actions={(
            <>
              <button type="button" className="cz-chip" onClick={handleExportToExcel} disabled={isWaiting}>
                {t('common.export')}
                {exportIcon}
              </button>
              <button type="button" className="cz-chip red" onClick={() => navigate("/external-order")}>
                {t('orders.addExternalOrder') || 'Create Order'}
                {plusIcon}
              </button>
            </>
          )}
        >
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography sx={{ color: 'var(--tx2)' }}>{t('dateRange.selectDateRange')}</Typography>
                <DateRangePicker
                  startDate={startDate}
                  endDate={endDate}
                  onChange={handleDateRangeChange}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sx={{ ...centered_flex_box }}>
                <Button sx={{ ...main_button }} fullWidth onClick={() => clearAllFilters()}>{t('common.clear')}</Button>
              </Grid>
            </Grid>
          </Box>
        </SearchFilterBar>

        {selectedRowKeys.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 13, color: 'var(--tx2)' }}>
              {t('orders.selectedOrders', { count: selectedRowKeys.length })}
            </div>
            <button
              type="button"
              className="cz-chip red"
              disabled={isWaiting}
              onClick={() => setCancelConfirmOpen(true)}
            >
              {t('orders.cancelSelected', { count: selectedRowKeys.length })}
            </button>
          </div>
        )}

        <Box sx={{
          width: "100%",
          overflowX: "auto",
          "& .ant-table-wrapper": { width: "100%" },
          "& .ant-table": { minWidth: isMobile ? 300 : 1480 },
          "& .ant-table-cell": { whiteSpace: isMobile ? "normal" : "nowrap" }
        }}>
          <Table
            columns={columns}
            dataSource={data}
            loading={isLoading}
            rowKey="_id"
            rowSelection={{
              selectedRowKeys,
              onChange: setSelectedRowKeys,
              getCheckboxProps: (record) => ({
                disabled: !CANCELLABLE_ORDER_STATUSES.includes(record.orderStatus),
              }),
            }}
            expandable={{
              expandedRowRender,
              rowExpandable: (record) => (record.items?.length > 0),
            }}
            pagination={{
              current: page,
              pageSize: pageSize,
              total: orders?.totalDocs,
              showSizeChanger: false,
              showQuickJumper: false,
              showTotal: (total) => `${t('table.total')} ${total} ${t('table.items') || 'items'}`,
            }}
            onChange={handleTableChange}
            showSorterTooltip={{
              target: 'sorter-icon',
            }}
            scroll={{ x: isMobile ? 300 : 1480 }}
          />
        </Box>
      </Box>
      <Dialog
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            backgroundColor: "var(--appBg)",
            color: "var(--secColor4)",
          },
        }}
      >
        <DialogTitle sx={{ backgroundColor: "var(--appBg)", color: "var(--secColor4)", pb: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" sx={{ color: "var(--secColor4)", fontWeight: "bold" }}>
              {t("orders.exportOrders")}
            </Typography>
            <MUIButton
              onClick={() => setExportModalOpen(false)}
              sx={{ minWidth: "auto", p: 0.5, color: "var(--secColor4)" }}
            >
              <Close />
            </MUIButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: "var(--appBg)", color: "var(--secColor4)" }}>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: "var(--textSecondary)", mb: 0.5 }}>
                  {t("dateRange.selectDateRange")}
                </Typography>
                <DateRangePicker
                  startDate={exportParams.startDate}
                  endDate={exportParams.endDate}
                  onChange={handleExportDateRangeChange}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: "var(--textSecondary)", mb: 0.5 }}>
                  {t("common.status")}
                </Typography>
                <MainInput
                  fullWidth
                  value={exportParams.status}
                  onChange={({ target }) => handleExportStatusChange(target.value)}
                  displayEmpty
                  select
                  sx={{ mt: 1 }}
                >
                  <MenuItem value="">{t('orders.allStatuses') || 'All Statuses'}</MenuItem>
                  <MenuItem value="New">{t('orders.statusNew') || 'New'}</MenuItem>
                  <MenuItem value="Preparing">{t('orders.statusPreparing') || 'Preparing'}</MenuItem>
                  <MenuItem value="Prepared">{t('orders.statusPrepared') || 'Prepared'}</MenuItem>
                  <MenuItem value="In Transit">{t('orders.statusOutForDelivery') || 'Out for Delivery'}</MenuItem>
                  <MenuItem value="Delivered">{t('orders.delivered')}</MenuItem>
                  <MenuItem value="Cancelled">{t('orders.statusCancelled') || 'Cancelled'}</MenuItem>
                  <MenuItem value="Pending Confirmation">Pending Confirmation</MenuItem>
                  <MenuItem value="Unreachable">Unreachable</MenuItem>
                  <MenuItem value="RTO">RTO</MenuItem>
                  <MenuItem value="Exchanged">{t('orders.statusExchanged') || 'Exchanged'}</MenuItem>
                  <MenuItem value="Sold Out">{t('orders.statusSoldOut') || 'Sold Out'}</MenuItem>
                  <MenuItem value="Invalid">{t('orders.statusInvalid') || 'Invalid'}</MenuItem>
                </MainInput>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1, backgroundColor: "var(--appBg)" }}>
          <MUIButton
            onClick={() => setExportModalOpen(false)}
            sx={{ color: "var(--secColor4)" }}
          >
            {t("common.cancel")}
          </MUIButton>
          <MUIButton
            onClick={handleExportConfirm}
            disabled={
              !exportParams.startDate ||
              !exportParams.endDate ||
              isWaiting ||
              (exportParams.startDate && !exportParams.startDate.isValid()) ||
              (exportParams.endDate && !exportParams.endDate.isValid())
            }
            sx={{
              ...main_button,
              textTransform: "none",
            }}
          >
            {isWaiting ? t("transactions.exporting") : t("common.export")}
          </MUIButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cancelConfirmOpen}
        onClose={() => !isWaiting && setCancelConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            backgroundColor: "var(--appBg)",
            color: "var(--secColor4)",
          },
        }}
      >
        <DialogTitle sx={{ backgroundColor: "var(--appBg)", color: "var(--secColor4)" }}>
          {t("orders.cancelOrder")}
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: "var(--appBg)", color: "var(--secColor4)" }}>
          <Typography sx={{ color: "var(--secColor4)", mt: 1 }}>
            {t("orders.cancelOrdersConfirm", { count: selectedRowKeys.length })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1, backgroundColor: "var(--appBg)" }}>
          <MUIButton
            onClick={() => setCancelConfirmOpen(false)}
            disabled={isWaiting}
            sx={{ color: "var(--secColor4)" }}
          >
            {t("common.cancel")}
          </MUIButton>
          <MUIButton
            onClick={handleBulkCancel}
            disabled={isWaiting || selectedRowKeys.length === 0}
            sx={{ ...main_button, textTransform: "none" }}
          >
            {isWaiting ? t("orderDetails.cancelling") : t("orders.cancelOrder")}
          </MUIButton>
        </DialogActions>
      </Dialog>

      <CreateTicketModal
        open={helpTicketOpen}
        onClose={() => { setHelpTicketOpen(false); setHelpTicketContext(null); }}
        token={token}
        context={helpTicketContext}
        onSuccess={() => setHelpTicketOpen(false)}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state?.auth?.user,
  isLoading: state?.records?.isLoading,
  isWaiting: state?.wait?.isLoading,
  token: state?.auth?.token,
  orders: state?.records?.orders,
  orderStatistics: state?.records?.orderStatistics
});

const mapDispatchToProps = { getOrders, getOrderStatistics, getSddSettings, exportOrders, sendOrderConfirmation, cancelOrder };

export default connect(mapStateToProps, mapDispatchToProps)(Orders);
